import { type ReactNode } from "react";

import toast from "react-hot-toast";

import uuid from "uuid";

import { Segment } from "simplydo/react/ui";

import util from "utils/utils";

import { FileChooserWithDropzone } from "components/lib/Choosers";

import { FieldProps, IdeaFileValue } from "./FieldProps";
import { FileComponent } from "./File";

type DropZoneProps = {
  children: React.ReactNode;
  files: IdeaFileValue[];
  onRemove: (fileValue: string) => void;
  onClick: () => void;
};

const DropZone = ({ children, files, onRemove, onClick }: DropZoneProps) => {
  return (
    <Segment placeholder onClick={onClick}>
      {children}
      {files?.length > 0 ?
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <FileComponent files={files} onRemove={onRemove} />
        </div>
      : null}
    </Segment>
  );
};

const MultiFile = ({
  field,
  fieldValue = [],
  updateIdea,
  isEditing,
  ideaId,
}: FieldProps) => {
  const files = (
    Array.isArray(fieldValue) ? fieldValue : []) as IdeaFileValue[];

  const addFiles = (
    uploadedFiles: {
      savedName: string;
      fileName: string;
      signedDownloadRequest: string;
    }[],
  ) => {
    if (
      field.maxFiles &&
      files.length + uploadedFiles.length > field.maxFiles
    ) {
      toast.error("Your uploads exceed the maximum number of files allowed");
      return;
    }

    const updatedFiles = [
      ...uploadedFiles.map((file) => ({
        value: file.signedDownloadRequest,
        name: file.fileName,
        _id: uuid.v4(),
      })),
      ...files,
    ];
    updateIdea(updatedFiles, ["templated", field.id]);
  };

  const removeFile = (fileValue: string) => {
    util
      .confirm(
        "Are you sure you want to delete this file?",
        "This action cannot be undone, but you can always re-upload the file.",
      )
      .then(() => {
        const updatedImages = files.filter((file) => file.value !== fileValue);
        updateIdea(updatedImages, ["templated", field.id]);
      })
      .catch(() => {});
  };

  const hasMaxFiles = !!(field.maxFiles && files.length >= field.maxFiles);

  if (!isEditing) {
    return <FileComponent files={files} />;
  }

  const MultiFileDropzone = ({
    children,
    onClick,
  }: {
    children: ReactNode;
    onClick: () => void;
  }) => (
    <DropZone files={files} onRemove={removeFile} onClick={onClick}>
      {children}
    </DropZone>
  );

  return (
    <div>
      <FileChooserWithDropzone
        forType="idea"
        forId={ideaId}
        allowMultiple={true}
        onCompleteMultiple={addFiles}
        onError={toast.error}
        popupProps={{ position: "bottom center", disabled: hasMaxFiles }}
        dropzoneProps={{
          title: "Upload multiple files",
          subtitle:
            field.maxFiles ?
              `Maximum of ${util.pluralise(field.maxFiles, "file", "files")}`
            : undefined,
          DropZoneComponent: MultiFileDropzone,
          defaultIcon: "folder",
        }}
      />
    </div>
  );
};

export default MultiFile;
